import { createFileRoute, redirect } from '@tanstack/react-router'
import { databases } from '@/lib/appwrite'
import { Query } from 'appwrite'
import type { ServiceReport } from '@/Wist-blockchain/collections/serviceReport/types'
import type { Instrument } from '@/Wist-blockchain/collections/instruments/types'
import type { Company } from '@/lib/collections/organizations/getOrganizationsFetch'
import ServiceReportDetail from '@/Wist-blockchain/serviceReport/view/user/detailServiceReport'

const DB      = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID
const DB_MAIN = import.meta.env.VITE_APPWRITE_DATABASE_ID

export const Route = createFileRoute('/_authenticated/instruments/report/$id/')({
    beforeLoad: ({ context }) => {
        if (!context.user) throw redirect({ to: '/login' })
        const { isServicioTecnico, isAdmin } = context.userRoles
        if (!isServicioTecnico && !isAdmin) throw redirect({ to: '/unauthorized' })
    },
    loader: async ({ params }) => {
        const report = await databases.getDocument(
            DB,
            import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID,
            params.id
        ) as unknown as ServiceReport

        const [instrument, activitiesRes, materialsRes, profileRes, previousRes] = await Promise.all([
            databases.getDocument(DB, import.meta.env.VITE_BLOCKCHAIN_INSTRUMENTS_COLLECTION_ID, report.idInstrument),
            databases.listDocuments(DB, import.meta.env.VITE_BLOCKCHAIN_REPORT_ACTIVITIES_COLLECTION_ID, [
                Query.equal("idReport", report.idReport),
                Query.orderAsc("order"),
            ]),
            databases.listDocuments(DB, import.meta.env.VITE_BLOCKCHAIN_REPORT_MATERIALS_COLLECTION_ID, [
                Query.equal("idReport", report.idReport),
                Query.orderAsc("order"),
            ]),
            report.engineerId
                ? databases.listDocuments(DB_MAIN, import.meta.env.VITE_WORKER_PROFILE_COLLECTION_ID, [
                    Query.equal("userId", report.engineerId),
                ])
                : Promise.resolve({ documents: [] }),
            databases.listDocuments(DB, import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID, [
                Query.equal("idInstrument", report.idInstrument),
                Query.equal("status", "final"),
                Query.notEqual("$id", params.id),
                Query.orderDesc("serviceDate"),
                Query.limit(1),
            ]),
        ])

        const typedInstrument = instrument as unknown as Instrument

        const [company, brandName] = await Promise.all([
            typedInstrument.idOrganization
                ? databases.getDocument(DB_MAIN, import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID, typedInstrument.idOrganization)
                    .then(d => d as unknown as Company)
                    .catch(() => undefined)
                : Promise.resolve(undefined),
            typedInstrument.idBrand
                ? databases.getDocument(DB, import.meta.env.VITE_BLOCKCHAIN_BRANDS, typedInstrument.idBrand)
                    .then(d => d.name as string)
                    .catch(() => null)
                : Promise.resolve(null),
        ])

        const profile = profileRes.documents[0]
        const engineerName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || report.engineerId || "—"
        const engineerProfile = {
            position: profile?.rolCorp ?? "—",
            idNumber: profile?.colabDniCode ?? "—",
            phone:    profile?.phone1 ?? "—",
            email:    profile?.email ?? "—",
        }

        const previousServiceInfo = {
            isFirst:  previousRes.total === 0,
            lastDate: (previousRes.documents[0]?.serviceDate as string | undefined) ?? null,
        }

        return {
            report,
            company,
            instrument: typedInstrument,
            brandName,
            activities:          activitiesRes.documents,
            materials:           materialsRes.documents,
            engineerName,
            engineerProfile,
            previousServiceInfo,
        }
    },
    errorComponent: () => <div>Reporte no encontrado.</div>,
    component: RouteComponent,
})

function RouteComponent() {
    const {
        report, company, instrument, brandName,
        activities, materials, engineerName, engineerProfile, previousServiceInfo,
    } = Route.useLoaderData()

    return (
        <ServiceReportDetail
            report={report}
            company={company}
            instrument={instrument}
            brandName={brandName}
            activities={activities}
            materials={materials}
            engineerName={engineerName}
            engineerProfile={engineerProfile}
            previousServiceInfo={previousServiceInfo}
        />
    )
}
